// src/components/races/detail/raceFiles.tsx
import { type RaceWithParticipants } from "@/backend/domain/race/getRace";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type RaceFileType } from "@/backend/domain/models/race";
import { FILE_TYPE_LABELS } from "@/backend/adapters/services/file/iFileStorageService";

interface RaceFilesProps {
    race: RaceWithParticipants;
}

export function RaceFiles({ race }: RaceFilesProps) {
    const filesByType = race.files.reduce((acc, file) => {
        if (!acc[file.type]) {
            acc[file.type] = [];
        }
        acc[file.type].push(file);
        return acc;
    }, {} as Record<RaceFileType, typeof race.files>);

    if (Object.keys(filesByType).length === 0) {
        return null;
    }

    return (
        <div className="border rounded-lg p-6 bg-slate-50">
            <div className="text-sm tracking-widest text-slate-600 mb-4">DOKUMENTE</div>
            <div className="flex flex-wrap gap-4">
                {Object.entries(filesByType).map(([type, files]) => (
                    <FileButton
                        key={type}
                        type={type as RaceFileType}
                        files={files}
                        raceId={race.id}
                    />
                ))}
            </div>
        </div>
    );
}

interface FileButtonProps {
    type: RaceFileType;
    files: RaceWithParticipants['files'];
    raceId: string;
}

function FileButton({ type, files, raceId }: FileButtonProps) {
    const isResults = type === 'RESULTS';
    const label = FILE_TYPE_LABELS[type];

    const formatDisplayFileName = (filename: string): string => filename.split('/').pop() || filename;

    if (files.length === 1) {
        return (
            <Button
                variant={isResults ? "default" : "outline"}
                size="lg"
                asChild
                className={`gap-2 ${
                    isResults
                        ? "bg-slate-900 hover:bg-slate-800"
                        : "hover:bg-slate-100 hover:border-slate-400"
                }`}
            >
                <a href={`/race/files/${raceId}/${files[0].id}`}>
                    <FileText className="h-5 w-5"/>
                    {label}
                    <Download className="h-4 w-4 ml-2"/>
                </a>
            </Button>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={isResults ? "default" : "outline"}
                    size="lg"
                    className={`gap-2 ${
                        isResults
                            ? "bg-slate-900 hover:bg-slate-800"
                            : "hover:bg-slate-100 hover:border-slate-400"
                    }`}
                >
                    <FileText className="h-5 w-5"/>
                    {label} ({files.length})
                    <Download className="h-4 w-4 ml-2"/>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[300px]">
                {files.map(file => (
                    <DropdownMenuItem key={file.id} asChild>
                        <a
                            href={`/race/files/${raceId}/${file.id}`}
                            className="flex items-center gap-2"
                        >
                            <FileText className="h-4 w-4"/>
                            <span className="truncate">
                                {formatDisplayFileName(file.filename)}
                            </span>
                        </a>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
